import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg bg-[#0A0C0E] border border-[#00A651]/20 px-4 py-2.5 text-sm text-[#F7F5F0] placeholder:text-[#8A99AD]/60 focus:outline-none focus:border-[#00A651]/60 transition-colors";

export default function DiasporaChapters() {
  const [chapters, setChapters] = useState(null);
  const [form, setForm] = useState({ city: "", country: "", leader_name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const load = () =>
    axios.get(`${API}/diaspora/chapters`).then((r) => setChapters(r.data)).catch(() => setChapters([]));

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city.trim() || !form.country.trim() || !form.leader_name.trim()) {
      toast.error("City, country, and chapter lead are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { city: form.city, country: form.country, leader_name: form.leader_name };
      if (form.email.trim()) payload.email = form.email.trim();
      await axios.post(`${API}/diaspora/chapters`, payload);
      toast.success(`${form.city} chapter registered with the Sixth Region.`);
      setForm({ city: "", country: "", leader_name: "", email: "" });
      load();
    } catch {
      toast.error("Could not register the chapter. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-[#4ADE80]/20 pt-5" data-testid="diaspora-chapters">
      <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#4ADE80]">
        City Chapters of the Sixth Region
      </p>

      <div className="mt-4 space-y-2.5 max-h-48 overflow-y-auto pr-1" data-testid="chapter-list">
        {chapters === null && <div className="animate-pulse rounded-lg border border-[#4ADE80]/10 bg-[#0A0C0E] h-14" />}
        {chapters !== null && chapters.length === 0 && (
          <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD]" data-testid="chapter-empty">
            No chapters yet — found the first one in your city.
          </p>
        )}
        {chapters !== null &&
          chapters.map((c, i) => (
            <div key={c.id} className="rounded-lg border border-[#4ADE80]/15 bg-[#0A0C0E] px-4 py-3 flex items-center gap-3" data-testid={`chapter-item-${i}`}>
              <MapPin className="w-3.5 h-3.5 text-[#4ADE80] shrink-0" />
              <div className="min-w-0">
                <p className="font-display font-bold text-sm text-[#F7F5F0] truncate">
                  {c.city} <span className="font-normal text-[#8A99AD]">— {c.country}</span>
                </p>
                <p className="font-official text-[9px] tracking-[0.15em] uppercase text-[#8A99AD] mt-0.5">
                  Led by {c.leader_name}
                </p>
              </div>
            </div>
          ))}
      </div>

      <form onSubmit={submit} className="mt-5 rounded-xl border border-[#4ADE80]/20 bg-[#0A0C0E] p-5 space-y-3" data-testid="chapter-register-form">
        <p className="font-official text-[9px] tracking-[0.25em] uppercase text-[#8A99AD]">
          Register your city chapter
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.city} onChange={set("city")} placeholder="City — e.g. London" className={inputCls} data-testid="chapter-city-input" />
          <input value={form.country} onChange={set("country")} placeholder="Country — e.g. United Kingdom" className={inputCls} data-testid="chapter-country-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.leader_name} onChange={set("leader_name")} placeholder="Chapter lead — your name" className={inputCls} data-testid="chapter-leader-input" />
          <input type="email" value={form.email} onChange={set("email")} placeholder="Email (optional)" className={inputCls} data-testid="chapter-email-input" />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="chapter-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4ADE80] text-[#0A0C0E] font-display font-bold text-xs px-6 py-3 hover:bg-[#6EE7B7] disabled:opacity-60 transition-all duration-300"
        >
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Found This Chapter
        </button>
      </form>
    </div>
  );
}
